import React, { useState } from "react";
import { Table, Input, Select, Button, Tag, Space, Popconfirm, Breadcrumb, Empty, Typography, Tooltip, Alert } from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    PlayCircleOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    HomeOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const statusColors = {
    success: "green",
    failed: "red",
    pending: "gold",
    running: "blue",
    never_executed: "default",
};

const statusLabels = {
    success: "Success",
    failed: "Failed",
    pending: "Pending",
    running: "Running",
    never_executed: "Never Executed",
};

export default function ViewsPage({
    views = [],
    searchTerm,
    onSearch,
    onFilterChange,
    onViewDetail,
    onCreateView,
    onDeleteView,
    onRunView,
    onViewSourceDataset,
    loading,
    permission,
    loadError,
}) {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const columns = [
        {
            title: "View Name",
            dataIndex: "viewName",
            key: "viewName",
            width: 220,
            render: (text, record) => (
                <a
                    className="views-table-link"
                    onClick={() => onViewDetail(record.viewId)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                    <EyeOutlined />
                    <span>{text}</span>
                </a>
            ),
            sorter: (a, b) => (a.viewName || "").localeCompare(b.viewName || ""),
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            width: 320,
            render: (text) => (
                <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{text}</span>
            ),
        },
        {
            title: "Source Dataset",
            key: "sourceDataset",
            width: 260,
            ellipsis: { showTitle: false },
            render: (_, record) => {
                const displayText = record.sourceDatasetName || record.sourceDatasetId;
                const tooltipText = record.sourceDatasetName
                    ? `${record.sourceDatasetName}\n(${record.sourceDatasetId})`
                    : record.sourceDatasetId;
                return (
                    <Tooltip placement="topLeft" title={tooltipText}>
                        <a
                            className="views-table-link"
                            onClick={() => onViewSourceDataset && onViewSourceDataset(record)}
                            style={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}
                        >
                            {displayText}
                        </a>
                    </Tooltip>
                );
            },
        },
        {
            title: "Steps",
            key: "steps",
            align: "center",
            width: 70,
            render: (_, record) => {
                const stepCount = record.pipelineStepsSnapshot ? record.pipelineStepsSnapshot.length : 0;
                return <Tag color="blue">{stepCount}</Tag>;
            },
        },
        {
            title: "Records",
            dataIndex: "recordCount",
            key: "recordCount",
            align: "center",
            width: 90,
            render: (count) => (count || 0).toLocaleString(),
            sorter: (a, b) => (a.recordCount || 0) - (b.recordCount || 0),
        },
        {
            title: "Last Executed",
            dataIndex: "lastExecuted",
            key: "lastExecuted",
            render: (date) => date ? new Date(date).toLocaleString() : "Never",
            sorter: (a, b) => new Date(a.lastExecuted || 0) - new Date(b.lastExecuted || 0),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            align: "center",
            render: (status) => (
                <Tag color={statusColors[status] || "default"}>
                    {statusLabels[status] || status}
                </Tag>
            ),
        },
        {
            title: "Created By",
            key: "createdBy",
            width: 150,
            ellipsis: { showTitle: false },
            render: (_, record) => {
                const displayName = record.createdByName || record.createdBy || "";
                const tooltipText = record.createdByName
                    ? `${record.createdByName}\n(${record.createdBy})`
                    : record.createdBy || "Unknown";
                return (
                    <Tooltip placement="topLeft" title={tooltipText}>
                        <span style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}>
                            {displayName}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, record) => (
                <Space size="small">
                    {permission?.canEdit && (
                        <Button
                            type="text"
                            size="small"
                            icon={<PlayCircleOutlined />}
                            title="Execute View"
                            onClick={() => onRunView(record.viewId)}
                        />
                    )}
                    {permission?.canDelete && (
                        <Popconfirm
                            title="Delete this view?"
                            onConfirm={() => onDeleteView(record.viewId)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                title="Delete View"
                                className="views-table-delete"
                                danger
                            />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    };

    return (
        <div className="flex flex-col h-full">
            <style>{`
                .ant-table-tbody > tr > td .views-table-link,
                .ant-table-tbody > tr > td .views-table-link:link,
                .ant-table-tbody > tr > td .views-table-link:visited,
                .ant-table-tbody > tr > td .views-table-link:hover,
                .ant-table-tbody > tr > td .views-table-link:active,
                .ant-table-tbody > tr > td .views-table-link .anticon,
                .ant-table-tbody > tr > td .views-table-link span,
                .ant-table-tbody > tr:hover > td .views-table-link,
                .ant-table-tbody > tr:hover > td .views-table-link .anticon,
                .ant-table-tbody > tr:hover > td .views-table-link span {
                    color: #1677ff !important;
                    cursor: pointer;
                }
                .ant-table-tbody > tr > td .views-table-link:hover,
                .ant-table-tbody > tr > td .views-table-link:hover .anticon,
                .ant-table-tbody > tr > td .views-table-link:hover span {
                    color: #4096ff !important;
                }
                .ant-table-tbody > tr > td .views-table-delete,
                .ant-table-tbody > tr > td .views-table-delete .anticon,
                .ant-table-tbody > tr:hover > td .views-table-delete,
                .ant-table-tbody > tr:hover > td .views-table-delete .anticon {
                    color: #ff4d4f !important;
                }
                .ant-table-tbody > tr > td .views-table-delete:hover,
                .ant-table-tbody > tr > td .views-table-delete:hover .anticon {
                    color: #ff7875 !important;
                }
            `}</style>
            {/* Breadcrumbs */}
            <div className="px-4 py-2 border-b border-gray-200">
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <HomeOutlined /> Home
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Views</Breadcrumb.Item>
                </Breadcrumb>
            </div>

            {/* Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h1 className="text-xl font-semibold m-0">Data Views</h1>
                        <p className="text-sm text-gray-500 mt-0.5 mb-0">
                            Saved transformation pipelines and their output data
                        </p>
                    </div>
                    {permission?.canAdd && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={onCreateView}
                        >
                            Create View
                        </Button>
                    )}
                </div>

                {/* Search and filters */}
                <div className="flex items-center gap-2">
                    <Input
                        placeholder="Search views by name or description..."
                        prefix={<SearchOutlined />}
                        value={searchTerm}
                        onChange={(e) => onSearch(e.target.value)}
                        allowClear
                        className="flex-1"
                        size="small"
                    />
                    <Select
                        defaultValue="all"
                        size="small"
                        style={{ width: 140 }}
                        onChange={(value) => onFilterChange("status", value)}
                    >
                        <Option value="all">All Status</Option>
                        <Option value="success">Success</Option>
                        <Option value="failed">Failed</Option>
                        <Option value="pending">Pending</Option>
                        <Option value="running">Running</Option>
                        <Option value="never_executed">Never Executed</Option>
                    </Select>
                </div>
            </div>

            {/* Stats bar */}
            <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-6 text-sm">
                <span className="text-gray-500">
                    Total Views: <strong>{views.length}</strong>
                </span>
                <span className="text-gray-500">
                    Selected: <strong>{selectedRowKeys.length}</strong>
                </span>
                <span className="text-gray-500">
                    Success: <strong className="text-green-600">{views.filter(v => v.status === "success").length}</strong>
                </span>
                <span className="text-gray-500">
                    Failed: <strong className="text-red-600">{views.filter(v => v.status === "failed").length}</strong>
                </span>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 py-2">
                {loadError && (
                    <Alert
                        type="error"
                        showIcon
                        message={loadError}
                        className="mb-2"
                    />
                )}
                <Table
                    columns={columns}
                    dataSource={views}
                    rowKey="viewId"
                    rowSelection={rowSelection}
                    size="small"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `${total} views`,
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span>
                                        {loadError
                                            ? loadError
                                            : "No views yet. Create one from the pipeline builder or use the Create View button."}
                                    </span>
                                }
                            />
                        ),
                    }}
                />
            </div>
        </div>
    );
}
